import TileLayer from 'ol/layer/Tile';
import TileSource from 'ol/source/Tile';
import GeomodTileWMS from '../sources/GeomodTileWMS';
import GeomodWMTS from '../sources/GeomodWMTS';
import GeomodTMS from '../sources/GeomodTMS';
import AutoRefreshStrategy from '../utils/AutoRefreshStrategy';

/**
 * The options passed to the super class to build it.
 * @type {Array<string>}
 */
const INHERITED_OPTIONS = ['type', 'filter', 'title', 'opacity', 'visible', 'extent', 'minResolution', 'maxResolution', 'preload', 'source', 'map', 'name'];

/**
 * @typedef {Object} GeomodTileLayerAdditionnalOptions
 * @property {string} [type] If base layer, value is base.
 * @property {string} [filter] Filter name of the layer group in layerswitcher.
 * @property {string} [title] The title of the layer group in the layer switcher.
 * @property {number} [autoRefresh] Duration between to refresh.
 * @property {string} sourceType The type source (TileWMTS or TileWMS).
 * @property {number} [opacity] Opacity (0, 1). Default 1
 * @property {boolean} [visible] Visibility. Default true
 * @property {import('ol/extent').Extent} [extent] The bounding extent for layer rendering. The layer will not be rendered outside of this extent.
 * @property {number} [minResolution] The minimum resolution (inclusive) at which this layer will be visible.
 * @property {number} [maxResolution] The maximum resolution (exclusive) below which this layer will be visible.
 * @property {number} [preload] Preload. Load low-resolution tiles up to preload levels. 0 means no preloading.
 * @property {import('ol/source/Tile').default} [source] Source for this layer.
 * @property {import('ol/PluggableMap').default} [map] Sets the layer as overlay on a map. The map will not manage this layer in its layers collection, and the layer will be rendered on top. This is useful for temporary layers. The standard way to add a layer to a map and have it managed by the map is to use module:ol/Map#addLayer.
 * @property {Map<string,string>} [additionals] Additionals parameters for the layer.
 */

/**
 * @typedef {GeomodTileLayerAdditionnalOptions|import("../sources/GeomodWMTS").GeomodWMTSOptions|import("../sources/GeomodTileWMS").GeomodTileWMSOptions} GeomodTileLayerOptions
 */

/**
 * The Geomod tile layer. It create a {@link gm.source.GeomodTileWMS} if source is not provided.
 * @extends {TileLayer}
 */
export default class GeomodTileLayer extends TileLayer {
  /**
     * @constructor
     * @param {GeomodTileLayerOptions} options The options to build
     */
  constructor(options = {}) {
    if (options.source === undefined || !(options.source instanceof TileSource)) {
      switch (options.sourceType) {
        case 'TileWMTS':
          options.source = new GeomodWMTS(options);
          break;
        case 'TileTMS':
          options.source = new GeomodTMS(options);
          break;
        case 'TileWMS':
        default:
          options.source = new GeomodTileWMS(options);
          break;
      }
    }

    const superOptions = {};
    for (const key of INHERITED_OPTIONS) {
      superOptions[key] = options[key];
    }
    super(superOptions);

    if (options.autoRefresh) {
      this.autoRefresh = new AutoRefreshStrategy(this, options.autoRefresh);
      this.autoRefresh.start();
    }

    if (options.additionals) {
      this.values_ = { ...options.additionals, ...this.values_ };
    }
  }

  /**
     * Add a parameter for the layer.
     * @param {string} paramName Parameter name.
     * @param {string} newValue Parameter value.
     */
  addParam(paramName, value) {
    const source = this.getSource();
    if (source.updateParams) {
      source.updateParams({ [paramName]: value });
    }
  }

  /**
     * Update a parameter if it exists.
     * @param {string} paramName Parameter name.
     * @param {string} newValue Parameter value.
     */
  updateParam(paramName, newValue) {
    const source = this.getSource();
    if (source.updateParams && source.getParams && source.getParams()[paramName]) {
      source.updateParams({ [paramName]: newValue });
    }
  }

  /**
     * Reomve a parameter if it exists.
     * @param {string} paramName Parameter name.
     */
  removeParam(paramName) {
    const source = this.getSource();
    if (source.updateParams && source.getParams) {
      delete source.getParams()[paramName];
      source.updateParams({});
    }
  }

  /**
     * Refresh the layer by requesting a new image in server.
     */
  refresh() {
    this.getSource().refresh();
  }

  /**
     * Get the layer name.
     * @returns {string} the layers name
     */
  getName() {
    let layerName;
    const source = this.getSource();
    if (source instanceof GeomodTileWMS) {
      layerName = source.getParams().LAYERS;
    } else if (source instanceof GeomodWMTS) {
      layerName = source.getLayer();
    } else if (source instanceof GeomodTMS) {
      layerName = source.getName();
    }
    return layerName;
  }

  /**
     * Get the layer tag.
     * @return {string} the layers tag
     */
  getTag() {
    return this.getSource().getTag();
  }
}
